import { motion } from "framer-motion";
import { ArrowUpRight, Home, DoorOpen, Wrench, UtensilsCrossed, HardHat } from "lucide-react";

const SERVICES = [
{
  id: "ext-01",
  title: "Home Extensions",
  description: "Transform your living space with expertly designed and built home extensions. From single-storey to multi-level, we deliver quality craftsmanship.",
  icon: Home,
  image: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/fp08lgvs_extensions.jpg"
},
{
  id: "con-02",
  title: "Conservatories",
  description: "Beautiful conservatories and orangeries that bring the outside in. Custom designs to complement your home.",
  icon: Home,
  image: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/olht3ntc_replaster.jpg"
},
{
  id: "win-03",
  title: "uPVC Windows & Doors",
  description: "FENSA-registered installation of high-quality uPVC windows and doors. Energy efficient and built to last.",
  icon: DoorOpen,
  image: "https://images.pexels.com/photos/323781/pexels-photo-323781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
},
{
  id: "kit-04",
  title: "Kitchens",
  description: "Complete kitchen design and installation. From modern minimalist to traditional styles, we create your perfect kitchen.",
  icon: UtensilsCrossed,
  image: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/nlxhapnp_kitchen2.jpg"
},
{
  id: "joi-05",
  title: "Joinery",
  description: "Expert joinery services including bespoke woodwork, staircases, and fitted furniture. Traditional craftsmanship meets modern design.",
  icon: Wrench,
  image: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/d5546g9j_cupboards.jpg"
},
{
  id: "rof-06",
  title: "Roofing",
  description: "Complete roofing services including repairs, replacements, and loft conversions. Protecting your home from the top down.",
  icon: HardHat,
  image: "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/qxkszh4u_roof.jpg"
}];


const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 }
  }
};

export const ServicesSection = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-slate-50">

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <div className="mb-16">
          <span className="tech-label text-royal mb-4 block">EXPERIENCE & QUALITY</span>
          <h2
            data-testid="services-heading"
            className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">

            Our Services
          </h2>
          <p className="text-lg text-slate-600 max-w-none">
            From initial consultation to project completion, we manage every aspect of your building or joinery project with precision and care.
          </p>
        </div>

        {/* Services Grid - Uniform height */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          {SERVICES.map((service) =>
          <motion.div
            key={service.id}
            variants={cardVariants}
            data-testid={`service-card-${service.id}`}
            className="service-card group relative overflow-hidden border border-slate-200 bg-white flex flex-col h-full">

              {/* Image */}
              <div className="relative overflow-hidden h-48">
                <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover image-zoom" />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                
                {/* Technical Label */}
                <span className="absolute top-4 left-4 font-mono text-xs text-white/70 uppercase tracking-widest">
                  {service.id}
                </span>
                
                {/* Icon */}
                <div className="absolute bottom-4 left-4 w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur-sm">
                  <service.icon size={20} className="text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="font-heading font-semibold text-slate-900 mb-3 flex items-center justify-between text-xl">
                  {service.title}
                  <ArrowUpRight
                  size={20}
                  className="text-slate-400 group-hover:text-royal group-hover:translate-x-1 group-hover:-translate-y-1 transition-all flex-shrink-0" />

                </h3>
                <p className="text-slate-600 text-sm leading-relaxed flex-grow">
                  {service.description}
                </p>
              </div>

              {/* Hover Border Effect */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-royal/30 transition-colors pointer-events-none" />
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};